import { useState, useEffect, useRef } from "react";
import { authFetch } from "../App";
import { toast } from "sonner";
import { Image as ImageIcon, Upload, Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { invalidateAppDesign } from "../components/AppBackground";

const API = "/api";

const FILTERS = [
  { value: "none", label: "Keiner" },
  { value: "blue", label: "Kühl (Blau)" },
  { value: "warm", label: "Warm (Sepia)" },
  { value: "grey", label: "Grau (Dezent)" },
];

export default function AppDesignSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [gallery, setGallery] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(true);
  const fileRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [res, gres] = await Promise.all([
          authFetch(`${API}/app-design`),
          authFetch(`${API}/app-design/gallery`),
        ]);
        if (res.ok) {
          const data = await res.json();
          if (mounted) setCfg(data.config);
        }
        if (gres.ok) {
          const gdata = await gres.json();
          if (mounted) setGallery(gdata.items || []);
        }
      } catch {
        toast.error("Konfiguration konnte nicht geladen werden");
      } finally {
        if (mounted) setGalleryLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (!cfg) return null;

  const update = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const selectGallery = async (g) => {
    update({ background_url: g.url, background_id: g.id });
    try {
      const res = await authFetch(`${API}/app-design`, {
        method: "PUT",
        body: JSON.stringify({ config: { ...cfg, background_url: g.url, background_id: g.id } }),
      });
      if (res.ok) {
        const sd = await res.json();
        setCfg(sd.config);
        invalidateAppDesign(sd.config);
        toast.success(`Hintergrund "${g.name}" aktiv`);
      }
    } catch {
      toast.error("Fehler beim Aktivieren");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/app-design`, {
        method: "PUT",
        body: JSON.stringify({ config: cfg }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCfg(data.config);
      invalidateAppDesign(data.config);
      toast.success("Design gespeichert");
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const onPickImage = () => fileRef.current?.click();

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/app-design/background-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Upload fehlgeschlagen");
      }
      const data = await res.json();
      // Alle Variant-URLs + background_id ins lokale State übernehmen
      const patch = {
        background_url: data.background_desktop_url || data.url,
        background_id: data.id,
        background_original_url: data.background_original_url || null,
        background_mobile_url: data.background_mobile_url || null,
        background_desktop_url: data.background_desktop_url || null,
        background_homepage_url: data.background_homepage_url || null,
        background_thumbnail_url: data.background_thumbnail_url || null,
      };
      update(patch);
      // Sofort speichern, damit Bild aktiv wird (Backend ergänzt Variant-URLs auch serverseitig)
      const saveRes = await authFetch(`${API}/app-design`, {
        method: "PUT",
        body: JSON.stringify({ config: { ...cfg, ...patch } }),
      });
      if (saveRes.ok) {
        const sd = await saveRes.json();
        setCfg(sd.config);
        invalidateAppDesign(sd.config);
      }
      toast.success("Hintergrund hochgeladen");
      // Galerie neu laden
      try {
        const gres = await authFetch(`${API}/app-design/gallery`);
        if (gres.ok) {
          const gdata = await gres.json();
          setGallery(gdata.items || []);
        }
      } catch { /* noop */ }
    } catch (err) {
      toast.error(err.message || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeBg = async () => {
    update({ background_url: null, background_id: null });
    const res = await authFetch(`${API}/app-design`, {
      method: "PUT",
      body: JSON.stringify({ config: { ...cfg, background_url: null, background_id: null } }),
    });
    if (res.ok) {
      const sd = await res.json();
      setCfg(sd.config);
      invalidateAppDesign(sd.config);
      toast.success("Hintergrund entfernt");
    }
  };

  return (
    <div className="rounded-2xl p-5 space-y-5" style={{ background: "var(--tga-bg-card)", border: "1px solid var(--tga-border)" }} data-testid="app-design-settings">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold" style={{ color: "var(--tga-text-primary)" }}>App Design</h2>
          <p className="text-xs" style={{ color: "var(--tga-text-muted)" }}>Hintergrundbild + Glass-Effekte (gilt für alle Nutzer)</p>
        </div>
        <button
          type="button"
          onClick={() => setShowPreview((s) => !s)}
          className="p-2 rounded-lg hover:bg-white/5"
          title={showPreview ? "Vorschau ausblenden" : "Vorschau anzeigen"}
        >
          {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>

      {/* Hintergrundbild */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--tga-text-muted)" }}>Hintergrundbild</Label>
        <div className="flex items-center gap-3">
          {cfg.background_url ? (
            <div className="relative w-32 h-20 rounded-lg overflow-hidden border" style={{ borderColor: "var(--tga-border)" }}>
              <img src={cfg.background_url} alt="" className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-32 h-20 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(180deg, #0b1220 0%, #0f1b2e 100%)", border: "1px solid var(--tga-border)" }}>
              <ImageIcon className="w-6 h-6 text-gray-500" />
            </div>
          )}
          <div className="flex flex-col gap-2">
            <input type="file" ref={fileRef} accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden" onChange={onUpload} />
            <Button type="button" size="sm" variant="outline" onClick={onPickImage} disabled={uploading} data-testid="app-design-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {cfg.background_url ? "Ändern" : "Hochladen"}
            </Button>
            {cfg.background_url && (
              <Button type="button" size="sm" variant="ghost" onClick={removeBg} className="text-red-500">
                Entfernen
              </Button>
            )}
          </div>
        </div>
        <p className="text-[10px]" style={{ color: "var(--tga-text-muted)" }}>JPG, PNG, WebP oder HEIC – max. 10 MB. Automatische Optimierung für Mobile/Desktop/Homepage.</p>

        {/* Multi-Variant-Vorschau (Mobile/Desktop/Homepage) */}
        {cfg.background_id && (
          <div className="mt-3 grid grid-cols-3 gap-2" data-testid="variant-previews">
            {[
              { key: "mobile",   label: "Mobile",   ratio: "9 / 16", url: cfg.background_mobile_url || cfg.background_url },
              { key: "desktop",  label: "Desktop",  ratio: "16 / 9", url: cfg.background_desktop_url || cfg.background_url },
              { key: "homepage", label: "Homepage", ratio: "21 / 9", url: cfg.background_homepage_url || cfg.background_url },
            ].map((v) => (
              <div key={v.key} className="flex flex-col gap-1">
                <div className="rounded-md overflow-hidden border border-white/10" style={{ aspectRatio: v.ratio, background: "rgba(0,0,0,0.3)" }}>
                  {v.url && <img src={v.url} alt={v.label} className="w-full h-full object-cover" />}
                </div>
                <span className="text-[10px] text-center" style={{ color: "var(--tga-text-muted)" }}>{v.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Galerie */}
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--tga-text-muted)" }}>Galerie (Default-Hintergründe)</Label>
        {galleryLoading ? (
          <div className="flex items-center gap-2 text-xs" style={{ color: "var(--tga-text-muted)" }}>
            <Loader2 className="w-3 h-3 animate-spin" /> Lädt…
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {gallery.map((g) => {
              const isActive = cfg.background_url === g.url;
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => selectGallery(g)}
                  className={`relative aspect-[16/10] rounded-lg overflow-hidden border-2 transition-all ${
                    isActive ? "border-blue-500 ring-2 ring-blue-500/40" : "border-transparent hover:border-white/30"
                  }`}
                  title={g.name}
                  data-testid={`app-design-gallery-${g.id}`}
                >
                  <img src={g.url} alt={g.name} className="w-full h-full object-cover" loading="lazy" />
                  {isActive && (
                    <div className="absolute inset-0 bg-blue-500/20 flex items-center justify-center">
                      <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">✓</div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Helligkeit */}
      <div>
        <Label className="text-xs uppercase tracking-wide flex items-center justify-between" style={{ color: "var(--tga-text-muted)" }}>
          <span>Helligkeit</span>
          <span className="font-mono">{Math.round(cfg.brightness * 100)}%</span>
        </Label>
        <input
          type="range"
          min="0.3"
          max="1.0"
          step="0.05"
          value={cfg.brightness}
          onChange={(e) => update({ brightness: parseFloat(e.target.value) })}
          className="w-full mt-1"
          data-testid="app-design-brightness"
        />
      </div>

      {/* Overlay */}
      <div>
        <Label className="text-xs uppercase tracking-wide flex items-center justify-between" style={{ color: "var(--tga-text-muted)" }}>
          <span>Dunkles Overlay</span>
          <span className="font-mono">{Math.round(cfg.overlay_opacity * 100)}%</span>
        </Label>
        <input
          type="range"
          min="0"
          max="0.7"
          step="0.05"
          value={cfg.overlay_opacity}
          onChange={(e) => update({ overlay_opacity: parseFloat(e.target.value) })}
          className="w-full mt-1"
          data-testid="app-design-overlay"
        />
      </div>

      {/* Lesbarkeit (Text-Kontrast / Mobile Chrome) */}
      <div>
        <Label className="text-xs uppercase tracking-wide flex items-center justify-between" style={{ color: "var(--tga-text-muted)" }}>
          <span>Lesbarkeit (Mobile)</span>
          <span className="font-mono">{Math.round((cfg.text_contrast ?? 0.6) * 100)}%</span>
        </Label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={cfg.text_contrast ?? 0.6}
          onChange={(e) => update({ text_contrast: parseFloat(e.target.value) })}
          className="w-full mt-1"
          data-testid="app-design-text-contrast"
        />
        <p className="text-[10px] mt-1" style={{ color: "var(--tga-text-muted)" }}>
          Stellt Header/Bottom-Nav-Dunkelheit + Text-Schatten auf Mobile ein. Höher = bessere Lesbarkeit auf hellen Fotos.
        </p>
      </div>

      {/* Blur */}
      <div>
        <Label className="text-xs uppercase tracking-wide flex items-center justify-between" style={{ color: "var(--tga-text-muted)" }}>
          <span>Weichzeichner</span>
          <span className="font-mono">{cfg.blur_px}px</span>
        </Label>
        <input
          type="range"
          min="0"
          max="20"
          step="1"
          value={cfg.blur_px}
          onChange={(e) => update({ blur_px: parseInt(e.target.value, 10) })}
          className="w-full mt-1"
          data-testid="app-design-blur"
        />
      </div>

      {/* Farbfilter */}
      <div>
        <Label className="text-xs uppercase tracking-wide" style={{ color: "var(--tga-text-muted)" }}>Farbfilter</Label>
        <div className="grid grid-cols-4 gap-2 mt-2">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => update({ color_filter: f.value })}
              className={`px-2 py-2 text-xs rounded-lg border transition-colors ${
                cfg.color_filter === f.value
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-transparent text-gray-300 hover:bg-white/5"
              }`}
              style={cfg.color_filter !== f.value ? { borderColor: "var(--tga-border)" } : undefined}
              data-testid={`app-design-filter-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Glass-Cards Toggle */}
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={!!cfg.card_glass}
          onChange={(e) => update({ card_glass: e.target.checked })}
          className="w-4 h-4"
          data-testid="app-design-glass-toggle"
        />
        <div>
          <div className="text-sm font-medium" style={{ color: "var(--tga-text-primary)" }}>Glass-Cards</div>
          <div className="text-[11px]" style={{ color: "var(--tga-text-muted)" }}>Karten schweben transparent über dem Hintergrund</div>
        </div>
      </label>

      <Button onClick={save} disabled={saving} className="w-full" data-testid="app-design-save">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        Speichern
      </Button>
    </div>
  );
}
